import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { CHART_DATE_DIMENSIONS, DATE_RANGE_TYPES } from 'src/app/constants';

import { ApiService } from './api.service';

/**
 * Metrics that can be tracked for AI usage
 */
export enum AiUsageMetric {
  TOKEN_COUNT = 'tokenCount',
  REQUEST_COUNT = 'requestCount',
}

/**
 * Dimensions by which results can be broken down
 */
export enum AiUsageDimension {
  CUSTOMER_ID = 'customerId',
  SUBMITTER_USER_ID = 'submitterUserId',
  SOURCE_TYPE = 'sourceType',
  TRIGGER = 'trigger',
  CUSTOMER_TAG_ID = 'customerTagId',
  TICKET_TYPE_ID = 'ticketTypeId',
  STATUS = 'status',
  PROJECT_ID = 'projectId',
  ACCOUNT_ID = 'accountId',
  ACCOUNT_TAG_ID = 'accountTagId',
  TICKET_TYPE_FIELDS = 'ticketTypeFields', // For custom fields
}

/**
 * Filter clause for narrowing down data
 * Matches backend ReportFilterClause interface
 */
export interface AiUsageFilterClause {
  field: string;
  operator: '==' | '!=';
  value: string | boolean | null | (string | boolean | null)[];
}

/**
 * Date range specification for absolute ranges
 */
export interface AbsoluteAiUsageDateRange {
  type: typeof DATE_RANGE_TYPES.ABSOLUTE;
  startDate: string;
  endDate: string;
  label?: string; // UI metadata
}

/**
 * Date range specification for relative ranges
 */
export interface RelativeAiUsageDateRange {
  type: typeof DATE_RANGE_TYPES.RELATIVE;
  period: (typeof CHART_DATE_DIMENSIONS)[keyof typeof CHART_DATE_DIMENSIONS];
  startIndex: number;
  endIndex: number;
  count?: number; // Alternative to endIndex
  label?: string; // UI metadata
}

/**
 * Date range specification - union type for type safety
 */
export type AiUsageDateRange = AbsoluteAiUsageDateRange | RelativeAiUsageDateRange;

/**
 * Query parameters for AI usage data
 */
export interface AiUsageQuery {
  metric: AiUsageMetric;
  dateDimension: (typeof CHART_DATE_DIMENSIONS)[keyof typeof CHART_DATE_DIMENSIONS];
  dateRange: AiUsageDateRange;
  timezone: string;
  dimensions?: string[]; // Array of AiUsageDimension values or custom field keys like 'ticketTypeFields.abc123'
  filters?: AiUsageFilterClause[];
}

/**
 * Time series response structure
 */
export interface AiUsageTimeSeriesResponse {
  dates: string[];
  lookups: Record<string, Record<string, string>>; // dimension -> (key -> label) mapping
  data: Record<string, Record<string, number>>; // dimensionValue -> (date -> metric value)
}

@Injectable({
  providedIn: 'root',
})
export class AiUsageService {
  constructor(private api: ApiService) {}

  /**
   * Fetches AI usage time series data from the backend
   */
  async getAiUsageTimeSeries(query: AiUsageQuery): Promise<AiUsageTimeSeriesResponse> {
    // Build request body matching backend expectations
    const requestBody = {
      metric: query.metric,
      dateDimension: query.dateDimension,
      dateRange: query.dateRange,
      timezone: query.timezone,
      dimensions: query.dimensions,
      filters: query.filters,
    };

    // Make POST request to backend endpoint (ApiService automatically adds auth headers)
    const response = await firstValueFrom(
      this.api.post<AiUsageTimeSeriesResponse>('/reporting/ai-usage-time-series', requestBody),
    );

    return response;
  }
}
